<script setup lang="ts">
import { useAuthStore } from '#modules/auth/store/auth';
import { storeToRefs } from 'pinia';
import contractorImage from '~/assets/img/landing/how_it_work_contractor.png';
import posterImage from '~/assets/img/landing/how_it_works_job_poster.png';
import { AppButton, AppTypography } from '~/components/base';

const activeTab = ref('posters');
const { hasLoggedInUser } = storeToRefs(useAuthStore());

// Preload images to prevent flicker on tab switch
onMounted(() => {
  const img1 = new Image();
  img1.src = posterImage;
  const img2 = new Image();
  img2.src = contractorImage;
});

const posterSteps = [
  { number: 1, text: 'Post your job for free' },
  { number: 2, text: 'Receive offers from contractors' },
  { number: 3, text: 'Accept the right offer and get the job done' },
];

const contractorSteps = [
  { number: 1, text: 'Browse available jobs' },
  { number: 2, text: 'Submit your offer (small fee per bid)' },
  { number: 3, text: 'Win work and connect directly with clients' },
];

const currentSteps = computed(() => {
  return activeTab.value === 'posters' ? posterSteps : contractorSteps;
});

const currentImage = computed(() => {
  return activeTab.value === 'posters' ? posterImage : contractorImage;
});

const buttonText = computed(() => {
  return activeTab.value === 'posters' ? 'Create a Job' : 'Find a Job';
});

const handleButtonClick = () => {
  // Determine the target page based on active tab
  const targetPage = activeTab.value === 'posters' ? '/jobs/create' : '/jobs';

  // Check if user is authenticated
  if (!hasLoggedInUser.value) {
    // Both "Create a Job" and "Find jobs" redirect to sign up
    navigateTo(`/register?redirect=${encodeURIComponent(targetPage)}`, { external: false, replace: false });
    return;
  }

  // User is authenticated, navigate directly to target
  navigateTo(targetPage, { external: false, replace: false });
};
</script>

<template>
  <v-container fluid class="how-it-works-section pa-10 rounded-4xl">
    <v-container class="px-md-6 pt-md-6 pb-md-4">
      <v-row no-gutters>
        <v-col cols="12" md="6" sm="12" class="text-center d-none d-sm-block mb-md-8 pr-md-14" order="1">
          <img :src="currentImage" alt="How it works" class="how-it-works-image rounded-3xl" />
        </v-col>

        <v-col cols="12" md="6" sm="12" order="2" order-md="2" class="flex">
          <h2 class="text-h3 text-white mb-4 how-it-works-title">How it works</h2>

          <div class="tab-buttons-container mb-10 d-inline-flex rounded-xl" style="background-color: #1a1a34">
            <AppButton
              size="small"
              :variant="activeTab === 'posters' ? 'flat' : 'text'"
              :color="activeTab === 'posters' ? 'white' : 'white'"
              class="no-hover tab-button-item"
              rounded
              @click="activeTab = 'posters'"
            >
              For Job Posters
            </AppButton>
            <AppButton
              size="small"
              :variant="activeTab === 'contractors' ? 'flat' : 'text'"
              :color="activeTab === 'contractors' ? 'white' : 'white'"
              class="no-hover tab-button-item"
              rounded
              @click="activeTab = 'contractors'"
            >
              For Contractors
            </AppButton>
          </div>

          <!-- Dynamic Steps -->
          <div>
            <v-card
              v-for="(step, index) in currentSteps"
              :key="step.number"
              color="dark-navy-light"
              flat
              class="bg-dark-navy-light"
              :class="index === currentSteps.length - 1 ? 'mb-5' : 'mb-3'"
            >
              <v-card-text class="d-flex align-center">
                <v-avatar color="yellow-darken-1" size="24" class="mr-4">
                  <span class="text-black font-weight-bold">{{ step.number }}</span>
                </v-avatar>
                <AppTypography type="body-1" class="text-white pa-0 my-3">{{ step.text }}</AppTypography>
              </v-card-text>
            </v-card>

            <AppButton
              color="yellow-darken-1"
              class="rounded-pill no-hover how-it-works-button"
              @click="handleButtonClick"
            >
              {{ buttonText }}
            </AppButton>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/fonts' as *;

.how-it-works-section {
  width: initial;
  background-color: #01011d;
  padding: 40px !important;
  @media (max-width: 600px) {
    padding: 30px 12px !important;
  }
}

.how-it-works-section.rounded-4xl {
  @media (max-width: 600px) {
    border-radius: 24px !important;
  }
}

.how-it-works-title {
  font-family: $font-family-display !important;
  font-weight: 700 !important;
}

.tab-buttons-container {
  // Mobile and tablet: full width with buttons at 50% each
  @media (max-width: 959px) {
    width: 100% !important;
    display: flex !important;
    justify-content: space-between;

    :deep(.v-btn) {
      flex: 1;
      width: 50% !important;
      border-radius: 12px !important;
    }

    .tab-button-item {
      flex: 1;
      width: 50% !important;
      border-radius: 24px !important;
    }
  }
}

.how-it-works-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  transition: opacity 0.2s ease-in-out;
}

// Responsive button width
:deep(.how-it-works-button) {
  width: 100% !important;

  // 600px and up (sm breakpoint)
  @media (min-width: 600px) {
    width: 250px !important;
  }
}

:deep(.v-btn) {
  text-transform: none !important;
}

// Force no-hover class to completely remove effects
:deep(.no-hover) {
  &:hover,
  &:focus,
  &:active {
    opacity: 1 !important;

    .v-btn__overlay {
      display: none !important;
      opacity: 0 !important;
      background: transparent !important;
    }

    .v-btn__content {
      color: inherit !important;
    }
  }

  // Explicit colors for each variant
  &.v-btn--variant-flat {
    &:hover,
    &:focus,
    &:active {
      background-color: rgb(255, 255, 255) !important;
      color: rgb(0, 0, 0) !important;

      .v-btn__overlay {
        display: none !important;
        opacity: 0 !important;
        background: transparent !important;
      }

      .v-btn__content {
        color: rgb(0, 0, 0) !important;
      }
    }
  }

  &.v-btn--variant-text {
    &:hover,
    &:focus,
    &:active {
      background-color: transparent !important;
      color: rgb(255, 255, 255) !important;

      .v-btn__overlay {
        display: none !important;
        opacity: 0 !important;
        background: transparent !important;
      }

      .v-btn__content {
        color: rgb(255, 255, 255) !important;
      }
    }
  }
}
</style>
